import fastDeepEqual from "fast-deep-equal"

// Tiptap's getJSON fills every node's default attrs (textAlign, class), while a
// stored doc written outside the editor (AI generation, translations, the API)
// omits them. Drop null attrs and empty attrs objects so the two compare equal,
// or every sync tick reads as a content change and rewrites the editor.
const normalizeDoc = (value: unknown): unknown => {
	if (Array.isArray(value)) return value.map(normalizeDoc)
	if (value === null || typeof value !== "object") return value

	const out: Record<string, unknown> = {}
	for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
		if (val === null) continue
		out[key] = normalizeDoc(val)
	}

	const attrs = out.attrs
	if (
		attrs &&
		typeof attrs === "object" &&
		!Array.isArray(attrs) &&
		Object.keys(attrs).length === 0
	) {
		delete out.attrs
	}
	return out
}

export const isSameEditorDoc = (a: unknown, b: unknown) =>
	fastDeepEqual(normalizeDoc(a), normalizeDoc(b))

// Gates the autosave debounce, which fires on every content/title/imageUrl
// change including the mount seed from the local (possibly stale) Zero replica.
// Only queued changes save: user edits, plus the AI-completion copy into the
// translation row. Mount seeds and backend echoes must never write back.
// The queued source is carried through so the version row names its author.
export type AutosaveSource = "USER" | "AI"

export interface AutosaveGate {
	queue(source?: AutosaveSource): void
	take(): AutosaveSource | null
}

export const createAutosaveGate = (): AutosaveGate => {
	let queued: AutosaveSource | null = null
	return {
		// A pending AI copy never relabels an edit the user made in the meantime.
		queue: (source = "USER") => {
			if (source === "AI" && queued === "USER") return
			queued = source
		},
		take: () => {
			const taken = queued
			queued = null
			return taken
		},
	}
}

// One save in flight at a time; a save requested meanwhile runs once
// afterwards, so a slow earlier save can never land after a newer one and put
// older content on the server. USER wins over AI as the queued source, same as
// the gate above.
export const createSaveQueue = (
	run: (source: AutosaveSource) => Promise<void>,
) => {
	let inFlight = false
	let pending: AutosaveSource | null = null
	const save = async (source: AutosaveSource = "USER"): Promise<void> => {
		if (inFlight) {
			pending = pending === "USER" ? "USER" : source
			return
		}
		inFlight = true
		try {
			await run(source)
		} finally {
			inFlight = false
			const next = pending
			pending = null
			if (next) void save(next)
		}
	}
	return save
}

// A translation row landing for the language already on screen (the id flips
// from the placeholder to the saved row) must not overwrite unsaved local
// content, e.g. a copilot rewrite the user just applied. Switching language
// always reloads, since the editor then shows a different document.
export const shouldApplyTranslationToEditor = ({
	languageChanged,
	isDirty,
}: {
	languageChanged: boolean
	isDirty: boolean
}) => languageChanged || !isDirty

// A language with no saved translation row needs a client-generated id. Reuse
// it per changelog + language until the row lands, or a second write inserts a
// duplicate row for the same language.
export const pickTranslationRowId = ({
	savedId,
	cache,
	cacheKey,
	generateId,
}: {
	savedId: string
	cache: Record<string, string>
	cacheKey: string
	generateId: () => string
}) => {
	if (!savedId.startsWith("temp-")) return savedId
	const pending = cache[cacheKey]
	if (pending) return pending
	const id = generateId()
	cache[cacheKey] = id
	return id
}
